import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDynamicContext } from "@dynamic-labs/sdk-react-core";

import {
  getCmsLinks,
  zendeskLogin,
  redirectToZendesk,
} from "../../common/utils";
import {
  setIsLogin,
  setUserEmail,
} from "../../pages/login/services/login.slice";
import { RootState } from "../../redux/store";
import { Env, Paths } from "../../common/constants";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";
import { MANUFACTURER_TYPE } from "../../common/constants/enum";
import { getCartHasItems } from "../../pages/cart/services/action";
import { setLoading, setSessionActive } from "../../redux/global.slice";
import { getMyProfileData } from "../../pages/my-profile/services/action";
import { setProfileFetched } from "../../pages/my-profile/services/slices";
import { getCompanyProfileData } from "../../pages/company-profile/services/action";
import {
  AnalyticsEvents,
  capturePosthogEvent,
} from "../../common/utils/analytics";

const useHeaderHelper = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { handleLogOut } = useDynamicContext();

  const { pathname } = useLocation();
  const [isConfirmationOpen, setConfirmationOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const { userId } = useAppSelector((state: RootState) => state.verifyEmail);

  const { hasCartItem } = useAppSelector((state: RootState) => state.cart);

  const { myProfileData } = useAppSelector(
    (state: RootState) => state.myProfile,
  );

  const { userType, permissions, email: userEmail } = myProfileData;

  const isUserAdmin = userType === MANUFACTURER_TYPE.MANUFACTURER_USER;

  const canViewCompanyProfile =
    isUserAdmin || Boolean(permissions?.company_profile?.view);

  const isCartNotVisible =
    userType === MANUFACTURER_TYPE.MANUFACTURER_EMPLOYEE &&
    !permissions.qr_code.edit;

  const handleConfirmationOpen = () => {
    setConfirmationOpen(true);
  };

  const handleConfirmationClose = () => {
    setConfirmationOpen(false);
  };

  const open = Boolean(anchorEl);
  // const openNotification = Boolean(anchorElNotification);
  const id = open ? "simple-popover" : undefined;
  // const idNotification = openNotification ? "simple-popover" : undefined;

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };
  // const handleClickNotification = (event: any) => {
  //   setAnchorElNotification(event.currentTarget);
  // };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // const handleCloseNotification = () => {
  //   setAnchorElNotification(null);
  // };

  const handleProfile = () => {
    navigate(Paths.MY_PROFILE);
    handleClose();
  };

  const handleSubscription = () => {
    navigate(Paths.MY_SUBSCRIPTION);
    handleClose();
  };

  const handleMyAccountClick = () => {
    navigate(Paths.ACCOUNT_LIST);
    handleClose();
  };

  const handleComapanyProfile = () => {
    handleClose();
    navigate(Paths.COMPANY_PROFILE);
  };

  const handleConfirmLogout = async () => {
    const doLogout = async () => {
      localStorage.clear();
      sessionStorage.removeItem("externalRedirectData");

      dispatch(setUserEmail(""));
      dispatch(setIsLogin(false));
      dispatch(setSessionActive(false));
      dispatch(setProfileFetched(false));

      await handleLogOut();
      navigate(Paths.LOGIN);
      handleConfirmationClose();
    };

    if (
      typeof window !== "undefined" &&
      typeof (window as any).saveProductTokenizeDraft === "function"
    ) {
      (window as any).saveProductTokenizeDraft(doLogout);
    } else {
      await doLogout();
    }
  };

  const checkProfileName = () => {
    if (myProfileData.name) {
      const name = myProfileData.name.split(" ");
      return name[0];
    }
  };

  const handleClickCartIcon = () => {
    navigate(Paths.CART);
  };

  const handleHelpCenterClick = () => {
    dispatch(
      zendeskLogin(userEmail, async (token) => {
        capturePosthogEvent(AnalyticsEvents.HELP_CENTER_OPENED, {
          user_id: userId,
          source_page: pathname,
        });
        dispatch(redirectToZendesk(token, Env.BRAND_SUPPORT_URL));
        dispatch(setLoading(false));
      }),
    );
  };

  useEffect(() => {
    if (userId) {
      dispatch(getMyProfileData(userId));
      dispatch(getCompanyProfileData(userId));
    }

    dispatch(getCartHasItems());
    // eslint-disable-next-line
  }, [pathname]);

  useEffect(() => {
    dispatch(getCmsLinks());
    // eslint-disable-next-line
  }, []);

  return {
    id,
    open,
    Paths,
    userId,
    pathname,
    anchorEl,
    hasCartItem,
    isUserAdmin,
    myProfileData,
    isCartNotVisible,
    isConfirmationOpen,
    canViewCompanyProfile,

    dispatch,
    navigate,
    handleClick,
    handleClose,
    handleProfile,
    checkProfileName,
    handleSubscription,
    handleConfirmLogout,
    handleClickCartIcon,
    handleMyAccountClick,
    handleComapanyProfile,
    handleHelpCenterClick,
    handleConfirmationOpen,
    handleConfirmationClose,
  };
};

export default useHeaderHelper;
